import { canv } from "@/canv/global";
import type { BoardEntity } from "@/canvasController/types";
import type { elementBase } from "@/elementBase/elementBase";
import { Group } from "@/group";
import type { EntityBaseState } from "@/shared/types/entityState";
import { selectionManager } from "@/tools/cursorTool";
import type { BuildEventAction, BuildEventStructure } from "./types";

export class BuildEvent {
    event: BuildEventStructure;

    constructor(action: BuildEventAction, entities: EntityBaseState[]) {
        this.event = {
            type: "build",
            action,
            entities,
        };
    }

    execEvent() {
        if (this.event.action === "create") {
            const heads = canv.canvasController.createBranch(this.event.entities as BoardEntity[]);
            for (let i = 0; i < heads.length; i++) {
                heads[i].collectNeedInitWidgets(canv.eventManager.needInitWidgets as elementBase[]);
                const headParent = canv.canvasController.FindByUID(
                    heads[i].parentUID,
                ) as Group | null;
                // есть entity.parentUID == "", то тут будет null и это ок
                heads[i].setParent(headParent);
                heads[i].addToCollection();

                if (!heads[i].isItemWrapper()) {
                    heads[i].children.forEach((child) => {
                        if (child instanceof Group) {
                            child.addToCollection();
                        }
                    });
                }

                heads[i].fullUpdateDims();
            }
        } else {
            const UIDs = new Map();
            const states = this.event.entities;

            // наполняем мапу UID
            for (let i = 0; i < states.length; i++) {
                UIDs.set(states[i].UID, 0);
            }

            const headGroups: Group[] = [];

            for (let i = 0; i < states.length; i++) {
                const entity = canv.canvasController.FindByUID(states[i].UID) as Group; // в check уже проверили что есть
                if (!UIDs.has(entity.parentUID)) {
                    headGroups.push(entity);
                }
            }

            // тут кажется можно заменить на group.setParent(null).
            for (let i = 0; i < headGroups.length; i++) {
                const currentGroup = headGroups[i];

                if (this.event.action === "delete") {
                    currentGroup.beforeDeletion();
                }

                if (currentGroup.parent != null) {
                    for (let i = 0; i < currentGroup.parent.children.length; i++) {
                        if (currentGroup.parent.children[i] === currentGroup) {
                            currentGroup.parent.children.splice(i, 1);
                            break;
                        }
                    }
                }

                if (currentGroup.isItemWrapper()) {
                    for (let i = 0; i < canv.canvasController.items.length; i++) {
                        if (canv.canvasController.items[i] === currentGroup) {
                            canv.canvasController.items.splice(i, 1);
                            break;
                        }
                    }
                } else {
                    canv.canvasController.items = canv.canvasController.items.filter((item) => {
                        return !currentGroup.children.some((child) => child === item);
                    });

                    canv.canvasController.groups.delete(currentGroup.UID);
                }

                if (canv.canvasController.isInSelectedDeep(currentGroup)) {
                    // Делаем так, чтобы внутри softResetMode не сохранить interaction для уже удаленного entity
                    canv.toolBox.TryGetToolByName("cursorTool").mode = "";
                    selectionManager.clearSelected();
                }
            }
        }
    }

    checkEvent() {
        if (this.event.action === "delete") {
            const UIDs = new Map();

            for (let i = 0; i < this.event.entities.length; i++) {
                UIDs.set(this.event.entities[i].UID, 0);
                const enitity = canv.canvasController.FindByUID(this.event.entities[i].UID);
                if (enitity === null) {
                    return false;
                }
            }

            // проверяем, что не останется нерабочих ссылок
            for (let i = 0; i < this.event.entities.length; i++) {
                const entity = canv.canvasController.FindByUID(this.event.entities[i].UID) as Group;
                if (!entity.checkDeletion(UIDs)) {
                    return false;
                }
            }
        } else {
            for (let i = 0; i < this.event.entities.length; i++) {
                const entity = canv.canvasController.FindByUID(this.event.entities[i].UID);
                if (entity != null) {
                    return false;
                }
            }
        }

        return true;
    }

    getEvent() {
        return this.event;
    }

    loadEvent(event: BuildEventStructure) {
        this.event = event;
    }

    getTwin() {
        const isCreateEvent = this.event.action === "create";

        const action: BuildEventAction = isCreateEvent ? "delete" : "create";
        // заново собираем entity потому что у них может быть например другой parentUID
        const enitites: EntityBaseState[] = isCreateEvent ? this.event.entities : [];

        if (!isCreateEvent) {
            for (let i = 0; i < this.event.entities.length; i++) {
                const entity = canv.canvasController.FindByUID(this.event.entities[i].UID);
                if (entity && "getState" in entity && typeof entity.getState === "function") {
                    enitites.push(entity.getState("full") as EntityBaseState);
                }
            }
        }

        const twin = new BuildEvent(action, enitites);

        return twin;
    }
}
